import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAdminFetch } from "../../admin/hooks/useAdminFetch";
import { formatLabel, getDadRelationshipStage } from "../../admin/tabs/members/memberConstants";
import { BUCKET_ORDER, STAKEHOLDER_LANES } from "../config/stakeholderLaneConfig";
import { STAKEHOLDER_FOLLOW_UP_TYPE_LABELS } from "../config/stakeholderContactActions";
import { stakeholderMockItems } from "../mock/stakeholderMockData";
import { isOutreachQueueItem } from "../utils/outreachQueue";
import { orderDetailTagsForItem, sortItemsForLane } from "../utils/stakeholderPriority";

const STORAGE_KEY = "dp-stakeholder-manual-overrides-v1";

const DAD_STAGE_BUCKET_PRIORITY = {
  webinar_attended: 0,
  webinar_registered: 1,
  webinar_invited: 2,
  prospect: 3,
  dad_partners_founding_forty_joined: 4,
  member: 5,
};

const GUIDE_STATUS_BUCKET_PRIORITY = {
  scheduled: 0,
  active: 1,
  invited: 2,
  onboarding_sent: 3,
  prospect: 4,
};

const safeJsonParse = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const toDisplayText = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    for (const key of ["label", "name", "email", "phone", "value", "text"]) {
      const nested = value?.[key];
      if (typeof nested === "string" || typeof nested === "number" || typeof nested === "boolean") {
        return String(nested);
      }
    }
    return "";
  }
  return "";
};

const toDisplayDate = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    const parsedLocal = new Date(year, month - 1, day);
    if (Number.isNaN(parsedLocal.getTime())) return "";
    return parsedLocal.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const toDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDateKey = () => toDateKey(new Date());

const buildLatestContactSummary = (item) => {
  if (!item.latest_contact_label) return "";
  const atLabel = item.latest_contact_at ? toDisplayDate(item.latest_contact_at) : "";
  return atLabel
    ? `Last contact: ${item.latest_contact_label} ${atLabel}`
    : `Last contact: ${item.latest_contact_label}`;
};

const buildNextFollowUpSummary = (item) => {
  if (!item.follow_up_date && !item.follow_up_type_label) return "";
  const parts = [];
  if (item.follow_up_status === "overdue") parts.push("Overdue");
  if (item.follow_up_status === "due-today") parts.push("Due today");
  if (item.follow_up_type_label) parts.push(item.follow_up_type_label);
  if (item.follow_up_date_label) parts.push(item.follow_up_date_label);
  return `Next follow-up: ${parts.join(" ")}`.trim();
};

const deriveFollowUpStatus = (item) => {
  const followUpDateKey = toDateKey(item.follow_up_date);
  if (!followUpDateKey) return "";

  const todayDateKey = getTodayDateKey();
  if (followUpDateKey < todayDateKey) return "overdue";
  if (followUpDateKey === todayDateKey) return "due-today";
  return "upcoming";
};

const buildNeedsNowSummary = (item) => {
  const followUpLabel = item.follow_up_type_label || "Follow-up";
  const followUpDateLabel = item.follow_up_date_label || toDisplayDate(item.follow_up_date) || item.follow_up_date || "";
  const nextMove = item.next_move || "Reach out and reset the next step.";
  const dispositionPreviousState = (item.stakeholder_disposition_previous_state || "").trim().toLowerCase();
  const dispositionNewState = (item.stakeholder_disposition_new_state || "").trim().toLowerCase();

  if (item.follow_up_status === "overdue") {
    return `${followUpLabel} follow-up is overdue${followUpDateLabel ? ` from ${followUpDateLabel}` : ""}. ${nextMove}`;
  }
  if (item.follow_up_status === "due-today") {
    return `${followUpLabel} follow-up is due today. ${nextMove}`;
  }
  if (dispositionPreviousState === "dad_partner" && dispositionNewState === "prospect") {
    return "Returned from Dad Partners. Re-engage personally this week.";
  }
  if (item.urgency_override === "direct") {
    return `Direct Kevin follow-up is explicitly set. ${nextMove}`;
  }
  if (item.lane === "guide-partners" && item.guide_status_label === "Scheduled") {
    return "Scheduled conversation is coming up. Confirm prep and story focus.";
  }
  if (item.bucket === "needs-now") return nextMove;
  return "";
};

const titleFromRecord = (record) => toDisplayText(record?.name) || toDisplayText(record?.email) || "Unnamed record";

const uniqueCompact = (values) => [...new Set(values.filter(Boolean))].slice(0, 4);

const extractPhone = (record = {}) =>
  toDisplayText(record?.phone) ||
  toDisplayText(record?.phone_number) ||
  toDisplayText(record?.mobile) ||
  toDisplayText(record?.mobile_number) ||
  "";

const extractPhoto = (record = {}) =>
  record?.picture ||
  record?.photo_url ||
  record?.headshot_url ||
  record?.avatar_url ||
  record?.photo ||
  "";

const formatContactLabel = ({ contactLabel, contactPhone, contactEmail }) => {
  const nextContactLabel = toDisplayText(contactLabel);
  const nextContactPhone = toDisplayText(contactPhone);
  const nextContactEmail = toDisplayText(contactEmail);
  if (nextContactLabel) return nextContactLabel;
  if (nextContactPhone) return `Phone: ${nextContactPhone}`;
  if (nextContactEmail) return `Email: ${nextContactEmail}`;
  return "";
};

const deriveUrgencyLabel = (item) => {
  if (!item.is_kevin_flagged) return "";

  const nextMove = (item.next_move || "").toLowerCase();
  const reason = (item.reason || "").toLowerCase();
  const pressure = (item.dominant_pain_area || "").toLowerCase();

  switch (item.lane) {
    case "prospects":
      if (nextMove.includes("call")) return "Call";
      if (nextMove.includes("invite")) return "Invite";
      return "Direct";
    case "dad-partners":
      if (nextMove.includes("step in") || nextMove.includes("reframe") || /quiet|preach|culture/.test(`${reason} ${pressure}`)) {
        return "Intervene";
      }
      if (nextMove.includes("encourage")) return "Encourage";
      return "Direct";
    case "guide-partners":
      if (nextMove.includes("prep") || nextMove.includes("story")) return "Prep";
      if (nextMove.includes("follow up")) return "Follow up";
      return "Direct";
    case "support-partners":
      if (nextMove.includes("check-in") || nextMove.includes("call")) return "Care";
      return "Direct";
    case "operations":
      return "Resolve";
    case "super-fans":
      return "Reply";
    case "advisory-partners":
      return "Prepare";
    default:
      return "Direct";
  }
};

const buildContactLabel = (item) =>
  formatContactLabel({
    contactLabel: item.contact_label,
    contactPhone: item.contact_phone,
    contactEmail: item.contact_email,
  });

const applyOverrides = (items, overrides) =>
  items.map((item) => {
    const nextOverride = { ...(overrides[item.id] || {}) };
    delete nextOverride.is_kevin_flagged;

    return {
      ...item,
      ...nextOverride,
    };
  });

const withDerivedPresentationFields = (items) =>
  items.map((item) => ({
    ...item,
    detail_tags:
      item.detail_tags ||
      uniqueCompact([
        item.relationship_stage_label,
        item.warmth_label,
        item.dominant_pain_area,
        item.source_label,
        item.children_age_label,
        item.guide_status_label,
        item.invite_stage_label,
        item.scheduled_date_label ? `Scheduled ${item.scheduled_date_label}` : "",
        item.support_status_label,
        item.relationship_label,
        item.amplification_label,
        item.advisory_signal_label,
        item.type,
    ]),
    display_detail_tags: orderDetailTagsForItem(item).slice(0, 4),
    contact_label: buildContactLabel(item),
    follow_up_type_label:
      item.follow_up_type_label || STAKEHOLDER_FOLLOW_UP_TYPE_LABELS[item.follow_up_type] || "",
    follow_up_date_label: item.follow_up_date ? toDisplayDate(item.follow_up_date) || item.follow_up_date : "",
    urgency_label: deriveUrgencyLabel(item),
    latest_contact_summary: buildLatestContactSummary(item),
    next_follow_up_summary: buildNextFollowUpSummary({
      ...item,
      follow_up_type_label:
        item.follow_up_type_label || STAKEHOLDER_FOLLOW_UP_TYPE_LABELS[item.follow_up_type] || "",
      follow_up_date_label: item.follow_up_date ? toDisplayDate(item.follow_up_date) || item.follow_up_date : "",
    }),
  }));

const applyContactStates = (items, contactStates) =>
  items.map((item) => {
    const state = contactStates[item.id];
    if (!state) return item;
    return {
      ...item,
      follow_up_date: state.follow_up_date ?? item.follow_up_date ?? "",
      follow_up_type: state.follow_up_type ?? "",
      follow_up_type_label: state.follow_up_type_label ?? "",
      follow_up_note: state.follow_up_note ?? "",
      urgency_override: state.urgency_override ?? "",
      urgency_override_label: state.urgency_override_label ?? "",
      latest_contact_entry_id: state.latest_entry_id ?? "",
      latest_contact_action: state.latest_action_type ?? "",
      latest_contact_label: state.latest_action_label ?? "",
      latest_contact_note: state.latest_note ?? "",
      latest_contact_value: state.latest_contact_value ?? "",
      latest_contact_at: state.latest_created_at ?? "",
    };
  });

const applyAttentionSignals = (items, overrides = {}) =>
  items.map((item) => {
    const follow_up_type_label =
      item.follow_up_type_label || STAKEHOLDER_FOLLOW_UP_TYPE_LABELS[item.follow_up_type] || "";
    const follow_up_date_label = item.follow_up_date ? toDisplayDate(item.follow_up_date) || item.follow_up_date : "";
    const follow_up_status = deriveFollowUpStatus({
      ...item,
      follow_up_type_label,
      follow_up_date_label,
    });
    const hasBucketOverride = Object.prototype.hasOwnProperty.call(overrides[item.id] || {}, "bucket");
    const nextBucket =
      !hasBucketOverride && (follow_up_status === "overdue" || follow_up_status === "due-today")
        ? "needs-now"
        : item.bucket;
    const nextIsKevinFlagged =
      item.urgency_override === "direct"
        ? true
        : item.urgency_override === "not_direct"
          ? false
          : !!item.is_kevin_flagged;

    return {
      ...item,
      bucket: nextBucket,
      is_kevin_flagged: nextIsKevinFlagged,
      follow_up_status,
      follow_up_status_label:
        follow_up_status === "overdue"
          ? "Overdue"
          : follow_up_status === "due-today"
            ? "Due today"
            : follow_up_status === "upcoming"
              ? "Upcoming"
              : "",
      attention_priority_boost:
        follow_up_status === "overdue" ? 45 : follow_up_status === "due-today" ? 25 : 0,
      what_this_person_needs_now: buildNeedsNowSummary({
        ...item,
        bucket: nextBucket,
        is_kevin_flagged: nextIsKevinFlagged,
        follow_up_status,
        follow_up_type_label,
        follow_up_date_label,
      }),
    };
  });

const mergeRemoteAndStatic = (remoteItems, staticItems, remoteAvailability) => {
  const markedRemote = remoteItems.map((item) => ({
    ...item,
    is_mock_item: false,
  }));
  const filteredStatic = staticItems.filter((item) => {
    if (item.lane === "prospects" || item.lane === "dad-partners") return !remoteAvailability.members;
    if (item.lane === "guide-partners") return !remoteAvailability.guides;
    if (item.lane === "support-partners") return !remoteAvailability.support;
    if (item.lane === "operations") return !remoteAvailability.operations;
    return true;
  });
  const markedStatic = filteredStatic.map((item) => ({
    ...item,
    is_mock_item: true,
  }));
  return [...markedRemote, ...markedStatic];
};

const applyPersonRouteActionability = (items, isLoading) =>
  items.map((item) => {
    const disablePersonRouteActions = isLoading && item.is_mock_item;
    return {
      ...item,
      disable_person_route_actions: disablePersonRouteActions,
      person_route_action_note: disablePersonRouteActions
        ? "Live stakeholder data is still loading."
        : "",
    };
  });

const buildSourceKey = (sourceType, sourceId) =>
  sourceType && sourceId ? `${sourceType}:${sourceId}` : "";

const isDadRecord = (member) => (member?.tier || "").trim().toLowerCase() !== "guide_partner";

const isProspectStage = (stage) =>
  ["prospect", "webinar_invited", "webinar_registered", "webinar_attended"].includes(stage);

const isDadPartnerStage = (stage) =>
  ["dad_partners_founding_forty_joined", "member"].includes(stage);

const isActiveMemberForStakeholder = (member) =>
  (member?.status || "").trim().toLowerCase() !== "former";

const isGuideVisibleInStakeholder = (guide) =>
  !["declined", "backburner"].includes((guide?.invite_stage || "").trim().toLowerCase());

const deriveProspectBucket = (member) => {
  const stage = getDadRelationshipStage(member);
  const warmth = (member?.warmth || "").trim().toLowerCase();
  const dispositionPreviousState = (member?.stakeholder_disposition_previous_state || "").trim().toLowerCase();
  const dispositionNewState = (member?.stakeholder_disposition_new_state || "").trim().toLowerCase();

  if (dispositionPreviousState === "dad_partner" && dispositionNewState === "prospect") return "needs-now";
  if (warmth === "warm" || stage === "webinar_attended") return "needs-now";
  if (stage === "webinar_registered" || stage === "webinar_invited") return "moving";
  return "watch";
};

const deriveProspectNextMove = (member) => {
  const stage = getDadRelationshipStage(member);
  const warmth = (member?.warmth || "").trim().toLowerCase();
  const dispositionPreviousState = (member?.stakeholder_disposition_previous_state || "").trim().toLowerCase();
  const dispositionNewState = (member?.stakeholder_disposition_new_state || "").trim().toLowerCase();

  if (dispositionPreviousState === "dad_partner" && dispositionNewState === "prospect") return "Re-engage personally this week";
  if (warmth === "warm") return "Call this week";
  if (stage === "webinar_attended") return "Invite into the next concrete step";
  if (stage === "webinar_registered") return "Confirm next-step interest";
  if (stage === "webinar_invited") return "Send simple follow-up note";
  return "Offer a short support call";
};

const mapMembersToProspectItems = (members = []) => {
  return members
    .filter((member) => isDadRecord(member))
    .filter((member) => isActiveMemberForStakeholder(member))
    .filter((member) => isProspectStage(getDadRelationshipStage(member)))
    .sort((left, right) => {
      const leftStage = getDadRelationshipStage(left);
      const rightStage = getDadRelationshipStage(right);
      const leftPriority = DAD_STAGE_BUCKET_PRIORITY[leftStage] ?? 99;
      const rightPriority = DAD_STAGE_BUCKET_PRIORITY[rightStage] ?? 99;
      if (leftPriority !== rightPriority) return leftPriority - rightPriority;
      return (right.updated_at || right.created_at || "").localeCompare(left.updated_at || left.created_at || "");
    })
    .map((member) => {
      const stage = getDadRelationshipStage(member);
      const stageLabel = formatLabel(stage || "prospect");
      const warmth = member.warmth ? formatLabel(member.warmth) : "";
      const pressure = member.biggest_pressure_area ? formatLabel(member.biggest_pressure_area) : "";
      const source = member.source ? formatLabel(member.source) : "";
      const recentTransitionLabel =
        member.stakeholder_disposition_previous_state === "dad_partner" &&
        member.stakeholder_disposition_new_state === "prospect"
          ? "Returned From Dad Partners"
          : "";
      const parts = [recentTransitionLabel, stageLabel, warmth, pressure || source].filter(Boolean);

      return {
        id: `prospect-${member.user_id}`,
        lane: "prospects",
        source_type: "member",
        source_id: member.user_id,
        title: titleFromRecord(member),
        bucket: deriveProspectBucket(member),
        reason: parts.join(" | "),
        next_move: deriveProspectNextMove(member),
        last_meaningful_movement: `Current stage: ${stageLabel}${member.updated_at ? ` · updated ${toDisplayDate(member.updated_at)}` : ""}`,
        follow_up_date: "",
        notes:
          "This record is coming from the current members/admin data. Add stakeholder notes here to track tipping point, pain clarity, and next-step judgment.",
        is_kevin_flagged: deriveProspectBucket(member) === "needs-now",
        source,
        relationship_stage: stage,
        relationship_stage_label: stageLabel,
        warmth_label: warmth,
        dominant_pain_area: pressure,
        source_label: source,
        contact_phone: extractPhone(member),
        contact_email: member.email || "",
        photo_url: extractPhoto(member),
        detail_tags: uniqueCompact([recentTransitionLabel, stageLabel, warmth, pressure, source]),
      };
    });
};

const deriveDadPartnerBucket = (member) => {
  const stage = getDadRelationshipStage(member);
  if (stage === "dad_partners_founding_forty_joined") return "needs-now";
  if (stage === "member") return "moving";
  return "watch";
};

const deriveDadPartnerNextMove = (member) => {
  const stage = getDadRelationshipStage(member);
  if (stage === "dad_partners_founding_forty_joined") return "Encourage this week";
  return "Review for wins or drift";
};

const mapMembersToDadPartnerItems = (members = []) => {
  return members
    .filter((member) => isDadRecord(member))
    .filter((member) => isActiveMemberForStakeholder(member))
    .filter((member) => isDadPartnerStage(getDadRelationshipStage(member)))
    .sort((left, right) => {
      const leftStage = getDadRelationshipStage(left);
      const rightStage = getDadRelationshipStage(right);
      const leftPriority = DAD_STAGE_BUCKET_PRIORITY[leftStage] ?? 99;
      const rightPriority = DAD_STAGE_BUCKET_PRIORITY[rightStage] ?? 99;
      if (leftPriority !== rightPriority) return leftPriority - rightPriority;
      return (right.updated_at || right.created_at || "").localeCompare(left.updated_at || left.created_at || "");
    })
    .map((member) => {
      const stage = getDadRelationshipStage(member);
      const stageLabel = formatLabel(stage || "member");
      const pressure = member.biggest_pressure_area ? formatLabel(member.biggest_pressure_area) : "";
      const children = member.children_age_range ? formatLabel(member.children_age_range) : "";
      const parts = [stageLabel, pressure, children].filter(Boolean);

      return {
        id: `dad-${member.user_id}`,
        lane: "dad-partners",
        source_type: "member",
        source_id: member.user_id,
        title: titleFromRecord(member),
        bucket: deriveDadPartnerBucket(member),
        reason: parts.join(" | ") || stageLabel,
        next_move: deriveDadPartnerNextMove(member),
        last_meaningful_movement: `${stageLabel}${member.updated_at ? ` · updated ${toDisplayDate(member.updated_at)}` : ""}`,
        follow_up_date: "",
        notes:
          "This row is anchored to current dad/member records. Add wins, vulnerability, home movement, and culture notes here.",
        is_kevin_flagged:
          stage === "dad_partners_founding_forty_joined" ||
          /quiet|preach|culture/i.test(`${parts.join(" | ")} ${member.biggest_pressure_area || ""}`),
        relationship_stage: stage,
        relationship_stage_label: stageLabel,
        dominant_pain_area: pressure,
        children_age_label: children,
        contact_phone: extractPhone(member),
        contact_email: member.email || "",
        photo_url: extractPhoto(member),
        detail_tags: uniqueCompact([stageLabel, pressure, children]),
      };
    });
};

const deriveGuideBucket = (guide) => {
  const status = (guide?.status || "").trim().toLowerCase();
  const inviteStage = (guide?.invite_stage || "").trim().toLowerCase();

  if (status === "scheduled" || inviteStage === "scheduled") return "needs-now";
  if (status === "active" || inviteStage === "onboarding_sent" || inviteStage === "invited") return "moving";
  return "watch";
};

const deriveGuideNextMove = (guide) => {
  const status = (guide?.status || "").trim().toLowerCase();
  const inviteStage = (guide?.invite_stage || "").trim().toLowerCase();

  if (status === "scheduled" || inviteStage === "scheduled") return "Confirm prep and story focus";
  if (inviteStage === "not_invited") return "Decide whether to invite";
  if (inviteStage === "invited") return "Follow up on invitation";
  if (inviteStage === "onboarding_sent") return "Check onboarding progress";
  if (status === "active") return "Look for a useful way to activate this week";
  return "Review fit and next role";
};

const mapGuidesToItems = (guides = []) => {
  return guides
    .filter((guide) => isGuideVisibleInStakeholder(guide))
    .slice()
    .sort((left, right) => {
      const leftStatus = (left?.status || "").trim().toLowerCase();
      const rightStatus = (right?.status || "").trim().toLowerCase();
      const leftPriority = GUIDE_STATUS_BUCKET_PRIORITY[leftStatus] ?? 99;
      const rightPriority = GUIDE_STATUS_BUCKET_PRIORITY[rightStatus] ?? 99;
      if (leftPriority !== rightPriority) return leftPriority - rightPriority;
      return (right.updated_at || right.created_at || "").localeCompare(left.updated_at || left.created_at || "");
    })
    .slice(0, 12)
    .map((guide) => {
      const status = formatLabel((guide.status || "prospect").trim() || "prospect");
      const inviteStage = formatLabel((guide.invite_stage || "not_invited").trim() || "not_invited");
      const parts = [status, inviteStage].filter(Boolean);
      if (guide.scheduled_date) {
        parts.push(`Scheduled ${toDisplayDate(guide.scheduled_date)}`);
      }

      return {
        id: `guide-${guide.guide_id}`,
        lane: "guide-partners",
        source_type: "guide",
        source_id: guide.guide_id,
        title: titleFromRecord(guide),
        bucket: deriveGuideBucket(guide),
        reason: parts.join(" | "),
        next_move: deriveGuideNextMove(guide),
        last_meaningful_movement: `${status}${guide.updated_at ? ` · updated ${toDisplayDate(guide.updated_at)}` : ""}`,
        follow_up_date: "",
        notes:
          "Use stakeholder notes here for coaching, confidence, renewal, and how this person is actually serving.",
        is_kevin_flagged: deriveGuideBucket(guide) === "needs-now",
        guide_status_label: status,
        invite_stage_label: inviteStage,
        scheduled_date_label: guide.scheduled_date ? toDisplayDate(guide.scheduled_date) : "",
        contact_phone: extractPhone(guide),
        contact_email: guide.email || "",
        photo_url: extractPhoto(guide),
        detail_tags: uniqueCompact([
          status,
          inviteStage,
          guide.scheduled_date ? `Scheduled ${toDisplayDate(guide.scheduled_date)}` : "",
        ]),
      };
    });
};

const buildSupportItems = (overview) => {
  if (!overview) return [];

  const stats = overview?.stats || {};
  const supporters = Array.isArray(overview?.supporters) ? overview.supporters : [];
  const items = [];
  const pausedSupporter = supporters.find((supporter) => supporter?.support_status === "paused" || supporter?.support_status === "inactive");

  if (pausedSupporter) {
    items.push({
      id: `support-${pausedSupporter.supporter_id}`,
      lane: "support-partners",
      title: pausedSupporter.name || pausedSupporter.email || "Paused supporter",
      bucket: "needs-now",
      reason: `Support ${pausedSupporter.support_status || "paused"} | ${pausedSupporter.email || "No email"}`,
      next_move: "Make gracious personal check-in call",
      last_meaningful_movement: pausedSupporter.last_support_at
        ? `Last support ${toDisplayDate(pausedSupporter.last_support_at)}`
        : "Support appears paused",
      follow_up_date: "",
      notes: "Lead with care and curiosity, not pressure.",
      is_kevin_flagged: true,
      support_status_label: pausedSupporter.support_status || "paused",
      contact_phone: extractPhone(pausedSupporter),
      contact_email: pausedSupporter.email || "",
      photo_url: extractPhoto(pausedSupporter),
      detail_tags: uniqueCompact([
        pausedSupporter.support_status || "paused",
        pausedSupporter.email ? "Direct contact" : "",
      ]),
    });
  }

  items.push({
    id: "support-overview-active",
    lane: "support-partners",
    title: `${stats.total_supporters || 0} total supporters`,
    bucket: "moving",
    reason: `${stats.monthly_supporters || 0} monthly | ${stats.one_time_supporters || 0} one-time`,
    next_move: "Keep visible impact communication strong",
    last_meaningful_movement: `Lifetime support: $${Number(stats.lifetime_support_total || 0).toLocaleString()}`,
    follow_up_date: "",
    notes: "This is a simple live snapshot from mission support overview.",
    is_kevin_flagged: false,
    support_status_label: "active",
    detail_tags: uniqueCompact(["active", `${stats.monthly_supporters || 0} monthly`, `${stats.one_time_supporters || 0} one-time`]),
  });

  if ((stats.failed_syncs || 0) > 0) {
    items.push({
      id: "support-failed-syncs",
      lane: "support-partners",
      title: "Support sync issues",
      bucket: "watch",
      reason: `${stats.failed_syncs} failed sync${stats.failed_syncs === 1 ? "" : "s"}`,
      next_move: "Review sync health before trust softens",
      last_meaningful_movement: "Mission support overview is reporting failed syncs.",
      follow_up_date: "",
      notes: "The lane stays healthier when support records are trustworthy.",
      is_kevin_flagged: false,
      support_status_label: "watch",
      detail_tags: uniqueCompact(["watch", "sync issue"]),
    });
  }

  return items.slice(0, 6);
};

const buildOperationsItems = ({ stats, systemHealthOverview, supportOverview }) => {
  const items = [];
  const monitoringIncidents = Array.isArray(systemHealthOverview?.incidents)
    ? systemHealthOverview.incidents
    : [];
  const monitoringSnapshots = Array.isArray(systemHealthOverview?.snapshots)
    ? systemHealthOverview.snapshots
    : [];
  const degradedSnapshots = monitoringSnapshots.filter((snapshot) =>
    ["down", "degraded"].includes((snapshot?.status || snapshot?.aggregated_status || "").toLowerCase())
  );

  items.push({
    id: "operations-financial-condition",
    lane: "operations",
    title: "Financial condition",
    bucket: "needs-now",
    reason: supportOverview
      ? `${supportOverview?.stats?.monthly_supporters || 0} monthly supporters | $${Number(supportOverview?.stats?.lifetime_support_total || 0).toLocaleString()} lifetime support`
      : "Current financial picture still needs clean verification",
    next_move: "Verify current numbers this week",
    last_meaningful_movement: supportOverview
      ? "Mission support overview is available, but interpretation still belongs in Operations."
      : "No support overview data is loaded yet.",
    follow_up_date: "",
    notes: "This item stays in Needs Now until the board can answer financial questions cleanly.",
    is_kevin_flagged: true,
    type: "financial",
  });

  if (degradedSnapshots.length > 0 || monitoringIncidents.length > 0) {
    items.push({
      id: "operations-monitoring-attention",
      lane: "operations",
      title: "Monitoring attention",
      bucket: "needs-now",
      reason: `${degradedSnapshots.length} degraded/down snapshot${degradedSnapshots.length === 1 ? "" : "s"} | ${monitoringIncidents.length} incident${monitoringIncidents.length === 1 ? "" : "s"}`,
      next_move: "Review system health and confirm what is real",
      last_meaningful_movement: "System health overview is surfacing live attention items.",
      follow_up_date: "",
      notes: "Use this lane to decide whether the issue threatens trust or delivery this week.",
      is_kevin_flagged: true,
      type: "system",
    });
  } else {
    items.push({
      id: "operations-monitoring-steady",
      lane: "operations",
      title: "Monitoring status",
      bucket: "moving",
      reason: "No degraded snapshots or incidents are currently surfaced",
      next_move: "Keep weekly review rhythm visible",
      last_meaningful_movement: "System health overview is currently quiet.",
      follow_up_date: "",
      notes: "Quiet is good. Keep the trust layer visible without making it noisy.",
      is_kevin_flagged: false,
      type: "system",
    });
  }

  if (stats) {
    items.push({
      id: "operations-admin-stats",
      lane: "operations",
      title: "Admin stats snapshot",
      bucket: "moving",
      reason: `${stats.total_members || 0} members | ${stats.posts_today || 0} posts today | ${stats.total_recordings || 0} recordings`,
      next_move: "Use this as context, not as the primary operating truth",
      last_meaningful_movement: "Current admin stats are available for quick orientation.",
      follow_up_date: "",
      notes: "Stats alone do not tell the story, but they should stay visible.",
      is_kevin_flagged: false,
      type: "reporting",
    });
  }

  items.push({
    id: "operations-weekly-rhythm",
    lane: "operations",
    title: "Weekly review rhythm",
    bucket: "watch",
    reason: "Stakeholder review structure now exists, but not as a live generated report yet",
    next_move: "Turn this board truth into the Monday report next",
    last_meaningful_movement: "The stakeholder-first review structure is now implemented visually.",
    follow_up_date: "",
    notes: "This is the path to reducing mental load.",
    is_kevin_flagged: false,
    type: "reporting",
  });

  return items.slice(0, 8);
};

const deriveLaneStatus = (items, defaultStatus) => {
  if (items.some((item) => item.bucket === "needs-now")) return "needs-action";
  if (items.some((item) => item.bucket === "watch")) return "watch";
  return defaultStatus || "healthy";
};

export function useStakeholderBoardState() {
  const adminFetch = useAdminFetch();
  const lastRefreshAtRef = useRef(0);
  const [remoteState, setRemoteState] = useState({
    loading: true,
    members: [],
    membersLoaded: false,
    guides: [],
    guidesLoaded: false,
    stats: null,
    statsLoaded: false,
    supportOverview: null,
    supportLoaded: false,
    contactStates: {},
    contactStatesLoaded: false,
    dispositionStates: {},
    dispositionStatesLoaded: false,
    systemHealthOverview: null,
    systemHealthLoaded: false,
  });
  const [manualOverrides, setManualOverrides] = useState(() => {
    if (typeof window === "undefined") return {};
    return safeJsonParse(window.localStorage.getItem(STORAGE_KEY), {});
  });
  const [selectedItemId, setSelectedItemId] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(manualOverrides));
  }, [manualOverrides]);

  const fetchRemoteState = useCallback(async () => {
    lastRefreshAtRef.current = Date.now();
    setRemoteState((current) => ({ ...current, loading: true }));

    const [membersResult, guidesResult, statsResult, supportResult, contactStatesResult, dispositionStatesResult, systemHealthResult] = await Promise.allSettled([
      adminFetch("/admin/members"),
      adminFetch("/admin/guest-guides?status=all&sort_by=updated_at&sort_order=desc"),
      adminFetch("/admin/stats"),
      adminFetch("/admin/support/overview"),
      adminFetch("/admin/stakeholder/contacts/state"),
      adminFetch("/admin/stakeholder/dispositions/state"),
      adminFetch("/admin/system-health?history_hours=24&incident_limit=30"),
    ]);

    const nextState = {
      loading: false,
      members: [],
      membersLoaded: false,
      guides: [],
      guidesLoaded: false,
      stats: null,
      statsLoaded: false,
      supportOverview: null,
      supportLoaded: false,
      contactStates: {},
      contactStatesLoaded: false,
      dispositionStates: {},
      dispositionStatesLoaded: false,
      systemHealthOverview: null,
      systemHealthLoaded: false,
    };

    if (membersResult.status === "fulfilled" && membersResult.value.ok) {
      nextState.members = safeJsonParse(await membersResult.value.text(), []);
      nextState.membersLoaded = true;
    }

    if (guidesResult.status === "fulfilled" && guidesResult.value.ok) {
      const guidesJson = safeJsonParse(await guidesResult.value.text(), {});
      nextState.guides = Array.isArray(guidesJson.guides) ? guidesJson.guides : [];
      nextState.guidesLoaded = true;
    }

    if (statsResult.status === "fulfilled" && statsResult.value.ok) {
      nextState.stats = safeJsonParse(await statsResult.value.text(), null);
      nextState.statsLoaded = true;
    }

    if (supportResult.status === "fulfilled" && supportResult.value.ok) {
      nextState.supportOverview = safeJsonParse(await supportResult.value.text(), null);
      nextState.supportLoaded = true;
    }

    if (contactStatesResult.status === "fulfilled" && contactStatesResult.value.ok) {
      const contactStatesJson = safeJsonParse(await contactStatesResult.value.text(), {});
      const states = Array.isArray(contactStatesJson.states) ? contactStatesJson.states : [];
      nextState.contactStates = states.reduce((acc, state) => {
        if (state?.stakeholder_item_id) {
          acc[state.stakeholder_item_id] = state;
        }
        return acc;
      }, {});
      nextState.contactStatesLoaded = true;
    }

    if (dispositionStatesResult.status === "fulfilled" && dispositionStatesResult.value.ok) {
      const dispositionStatesJson = safeJsonParse(await dispositionStatesResult.value.text(), {});
      const states = Array.isArray(dispositionStatesJson.states) ? dispositionStatesJson.states : [];
      nextState.dispositionStates = states.reduce((acc, state) => {
        const sourceKey = state?.source_key || buildSourceKey(state?.source_type, state?.source_id);
        if (sourceKey) {
          acc[sourceKey] = state;
        }
        return acc;
      }, {});
      nextState.dispositionStatesLoaded = true;
    }

    if (systemHealthResult.status === "fulfilled" && systemHealthResult.value.ok) {
      nextState.systemHealthOverview = safeJsonParse(await systemHealthResult.value.text(), null);
      nextState.systemHealthLoaded = true;
    }

    setRemoteState(nextState);
  }, [adminFetch]);

  useEffect(() => {
    fetchRemoteState();
  }, [fetchRemoteState]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    const maybeRefresh = () => {
      if (document.hidden) return;
      if (Date.now() - lastRefreshAtRef.current < 5000) return;
      fetchRemoteState();
    };

    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);

    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
  }, [fetchRemoteState]);

  const remoteItems = useMemo(() => {
    const membersWithDispositionState = remoteState.members.map((member) => {
      const sourceKey = buildSourceKey("member", member?.user_id);
      const dispositionState = sourceKey ? remoteState.dispositionStates[sourceKey] : null;
      if (!dispositionState) return member;
      return {
        ...member,
        stakeholder_disposition_previous_state: dispositionState.previous_state || "",
        stakeholder_disposition_new_state: dispositionState.new_state || "",
      };
    });

    return [
      ...mapMembersToProspectItems(membersWithDispositionState),
      ...mapMembersToDadPartnerItems(membersWithDispositionState),
      ...mapGuidesToItems(remoteState.guides),
      ...buildSupportItems(remoteState.supportOverview),
      ...buildOperationsItems({
        stats: remoteState.stats,
        systemHealthOverview: remoteState.systemHealthOverview,
        supportOverview: remoteState.supportOverview,
      }),
    ];
  }, [
    remoteState.dispositionStates,
    remoteState.guides,
    remoteState.members,
    remoteState.stats,
    remoteState.supportOverview,
    remoteState.systemHealthOverview,
  ]);

  const items = useMemo(() => {
    const combined = mergeRemoteAndStatic(remoteItems, stakeholderMockItems, {
      members: remoteState.membersLoaded,
      guides: remoteState.guidesLoaded,
      support: remoteState.supportLoaded,
      operations:
        remoteState.statsLoaded || remoteState.systemHealthLoaded || remoteState.supportLoaded,
    });
    return applyPersonRouteActionability(
      withDerivedPresentationFields(
        applyAttentionSignals(
          applyOverrides(applyContactStates(combined, remoteState.contactStates), manualOverrides),
          manualOverrides
        )
      ),
      remoteState.loading
    );
  }, [
    manualOverrides,
    remoteItems,
    remoteState.contactStates,
    remoteState.guidesLoaded,
    remoteState.loading,
    remoteState.membersLoaded,
    remoteState.statsLoaded,
    remoteState.supportLoaded,
    remoteState.systemHealthLoaded,
  ]);

  const updateItem = (itemId, updates) => {
    setManualOverrides((current) => ({
      ...current,
      [itemId]: {
        ...(current[itemId] || {}),
        ...updates,
      },
    }));
  };

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId) || null,
    [items, selectedItemId]
  );

  const board = useMemo(() => {
    return STAKEHOLDER_LANES.map((lane) => {
      const laneItems = items.filter((item) => item.lane === lane.id);
      const grouped = BUCKET_ORDER.reduce((acc, bucket) => {
        acc[bucket] = sortItemsForLane(laneItems.filter((item) => item.bucket === bucket), lane.id);
        return acc;
      }, {});

      return {
        ...lane,
        status: deriveLaneStatus(laneItems, lane.status),
        counts: BUCKET_ORDER.reduce((acc, bucket) => {
          acc[bucket] = grouped[bucket].length;
          return acc;
        }, {}),
        overdueCount: laneItems.filter((item) => item.follow_up_status === "overdue").length,
        dueTodayCount: laneItems.filter((item) => item.follow_up_status === "due-today").length,
        items: grouped,
      };
    });
  }, [items]);

  const summary = useMemo(() => {
    const counts = BUCKET_ORDER.reduce((acc, bucket) => {
      acc[bucket] = items.filter((item) => item.bucket === bucket).length;
      return acc;
    }, {});

    return {
      counts,
      overdueCount: items.filter((item) => item.follow_up_status === "overdue").length,
      dueTodayCount: items.filter((item) => item.follow_up_status === "due-today").length,
      outreachQueueCount: items.filter((item) => isOutreachQueueItem(item)).length,
      overallStatus:
        counts["needs-now"] > 0 ? "needs-action" : counts.watch > 0 ? "watch" : "healthy",
      loading: remoteState.loading,
    };
  }, [items, remoteState.loading]);

  return {
    board,
    fetchRemoteState,
    items,
    selectedItem,
    selectedItemId,
    setSelectedItemId,
    summary,
    updateItem,
  };
}
